import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Users, Briefcase, MapPin, MessageSquare, ArrowRight, AlertCircle } from 'lucide-react';
import { countLocations } from '../../../services/locationsApi';
import { countSuggestions } from '../../../services/controlPanelApi';
import { countUsers } from '../../../services/usersApi';
import { countProviders } from '../../../services/providersApi';
import './AdminHome.css';

const KPI_DEFS = [
    { key: 'providers', label: 'Proveedores', icon: Briefcase, color: 'accent' },
    { key: 'users', label: 'Usuarios', icon: Users, color: 'info' },
    { key: 'locations', label: 'Locations', icon: MapPin, color: 'success' },
    { key: 'suggestions', label: 'Sugerencias sin leer', icon: MessageSquare, color: 'warning' },
];

const QUICK_LINKS = [
    { key: 'estadisticas', label: 'Ver estadísticas', description: 'KPIs y tendencias del negocio' },
    { key: 'proveedores', label: 'Gestionar proveedores', description: 'Activar, editar, dar de alta' },
    { key: 'sugerencias', label: 'Revisar sugerencias', description: 'Feedback de usuarios' },
    { key: 'notificaciones', label: 'Enviar notificación', description: 'Push a usuarios o proveedores' },
    { key: 'emails', label: 'Campaña de email', description: 'Comunicación masiva' },
    { key: 'categorias', label: 'Categorías', description: 'Catálogo y subcategorías' },
];

const formatNumber = (n) => {
    if (n == null) return '—';
    return new Intl.NumberFormat('es-AR').format(n);
};

const AdminHome = ({ onNavigate, onCountsChanged }) => {
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchCounts = useCallback(async () => {
        try {
            const [locRes, suggRes, userRes, provRes] = await Promise.all([
                countLocations(),
                countSuggestions(),
                countUsers(),
                countProviders(),
            ]);
            const next = {
                locations: locRes,
                suggestions: suggRes,
                users: userRes,
                providers: provRes,
            };
            setCounts(next);
            setError(null);
            if (onCountsChanged) onCountsChanged(next);
        } catch (err) {
            console.error('Error fetching counts:', err);
            setError('No se pudo cargar la información del resumen.');
        }
    }, [onCountsChanged]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            await fetchCounts();
            if (mounted) setLoading(false);
        };
        load();
        return () => {
            mounted = false;
        };
    }, [fetchCounts]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchCounts();
        setRefreshing(false);
    };

    const spActive = counts?.providers?.serviceProviderActive || 0;
    const spInactive = counts?.providers?.serviceProviderInactive || 0;
    const ppActive = counts?.providers?.productsProviderActive || 0;
    const ppInactive = counts?.providers?.productsProviderInactive || 0;
    const totalActive = spActive + ppActive;
    const totalInactive = spInactive + ppInactive;
    const totalProviders = totalActive + totalInactive;

    const kpiValues = {
        providers: { value: totalProviders, hint: `${formatNumber(totalActive)} activos · ${formatNumber(totalInactive)} inactivos` },
        users: { value: counts?.users, hint: 'Total registrados' },
        locations: { value: counts?.locations, hint: 'Zonas configuradas' },
        suggestions: {
            value: counts?.suggestions?.unseen ?? 0,
            hint: `${formatNumber(counts?.suggestions?.total || 0)} en total`,
        },
    };

    return (
        <div className="admin-home">
            <header className="admin-home__header">
                <div>
                    <h1 className="admin-home__title">Bienvenido al panel</h1>
                    <p className="admin-home__subtitle">Resumen general y accesos rápidos.</p>
                </div>
                <button
                    type="button"
                    className="admin-home__refresh-btn"
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    title="Actualizar conteos"
                >
                    <RefreshCw size={16} className={refreshing ? 'admin-spin' : ''} />
                    <span>Actualizar</span>
                </button>
            </header>

            {error && (
                <div className="admin-home__error" role="alert">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            <section className="admin-home__kpis" aria-label="KPIs principales">
                {KPI_DEFS.map(({ key, label, icon: Icon, color }) => {
                    const { value, hint } = kpiValues[key] || {};
                    return (
                        <article key={key} className={`admin-kpi admin-kpi--${color}`}>
                            <div className="admin-kpi__icon">
                                <Icon size={20} />
                            </div>
                            <div className="admin-kpi__body">
                                <div className="admin-kpi__label">{label}</div>
                                <div className="admin-kpi__value">
                                    {loading ? <span className="admin-kpi__skeleton" /> : formatNumber(value)}
                                </div>
                                {!loading && hint && <div className="admin-kpi__hint">{hint}</div>}
                            </div>
                        </article>
                    );
                })}
            </section>

            <section className="admin-home__providers-breakdown" aria-label="Desglose de proveedores">
                <h2 className="admin-home__section-title">Proveedores por tipo</h2>
                <div className="admin-providers-table-wrapper">
                    <table className="admin-providers-table">
                        <thead>
                            <tr>
                                <th>Tipo</th>
                                <th>Activos</th>
                                <th>Inactivos</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Servicio</td>
                                <td>{formatNumber(spActive)}</td>
                                <td>{formatNumber(spInactive)}</td>
                                <td>{formatNumber(spActive + spInactive)}</td>
                            </tr>
                            <tr>
                                <td>Productos</td>
                                <td>{formatNumber(ppActive)}</td>
                                <td>{formatNumber(ppInactive)}</td>
                                <td>{formatNumber(ppActive + ppInactive)}</td>
                            </tr>
                            <tr className="admin-providers-table__total">
                                <td>Total</td>
                                <td>{formatNumber(totalActive)}</td>
                                <td>{formatNumber(totalInactive)}</td>
                                <td>{formatNumber(totalProviders)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="admin-home__quick" aria-label="Accesos rápidos">
                <h2 className="admin-home__section-title">Accesos rápidos</h2>
                <div className="admin-quick-grid">
                    {QUICK_LINKS.map((link) => (
                        <button
                            key={link.key}
                            type="button"
                            className="admin-quick-card"
                            onClick={() => onNavigate(link.key)}
                        >
                            <div className="admin-quick-card__body">
                                <div className="admin-quick-card__label">{link.label}</div>
                                <div className="admin-quick-card__desc">{link.description}</div>
                            </div>
                            <ArrowRight size={18} className="admin-quick-card__arrow" />
                        </button>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default AdminHome;
